import { Location } from '@cobliteam/common-types';
import useUser from '@cobliteam/hooks/src/useEntity/useUser';
import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags';
import useGroupRecommendations from '@cobliteam/hooks/src/useGroupRecommendations/useGroupRecommendations';
import useIsGroupCountEligible from '@cobliteam/hooks/src/useGroupRecommendations/useIsGroupCountEligible';
import { useNudge } from '@cobliteam/hooks/src/useNudge';
import type { Nudge, NudgeSurface } from '@cobliteam/hooks/src/useNudge/types';
import { useTranslation } from '@cobliteam/i18n';
import NudgeMessage from '@cobliteam/ui-business/components/Nudge/NudgeMessage';
import { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

export const GROUP_NUDGE_ID = 'group-recommendations';

/**
 * Recommendation: groups are generated by an AI workflow, so this hits a back end.
 *
 * Visibility uses the CHEAP gate only — the `aiGroupRecommendations` flag plus the fleet-wide 0-1
 * group check. The expensive `generate` query is deferred until the nudge is expanded, because the
 * stack lives in the app shell: running it on mount would fire the workflow on every page of the
 * dashboard, which is the LLM-cost guardrail `useAiRecommendationCta` protects by deep-linking
 * instead of opening the chat. The e2e suite caught exactly that (`GenerateGroupRecommendation`
 * escaping the mock on a camera page).
 *
 * `useIsGroupCountEligible` is the count gate, written in AE-2569 for a floating card that shipped
 * without it and had been dead ever since.
 */
export const useGroupNudge = (surface: NudgeSurface = 'floating-card'): Nudge | null => {
  const { t } = useTranslation('groups');
  const { t: tHeader } = useTranslation('app-header');
  const history = useHistory();
  const { pathname } = useLocation();
  const { getSession } = useUser();
  const fleetId = getSession()?.fleet?.id;
  const { aiGroupRecommendations } = useFeatureFlags();

  const isOnOwnPage = pathname.startsWith(Location.VEHICLES_GROUPS);

  // Set by the stack through `onExpand`; until then the generate query stays disabled.
  const [expanded, setExpanded] = useState(false);

  const { isEligible } = useIsGroupCountEligible(fleetId, aiGroupRecommendations);

  const { shouldShow, dismiss, accept } = useNudge({
    id: GROUP_NUDGE_ID,
    kind: 'recommendation',
    surface,
    // Suppressed on the domain's own page, where the in-page banner already says this — the two
    // surfaces are complementary, not simultaneous. It also keeps the nudge's collapsed row from
    // duplicating the banner's copy, which is what the e2e suite flagged.
    useShouldShow: () => aiGroupRecommendations && isEligible && !isOnOwnPage,
  });

  const { data, isFetching } = useGroupRecommendations({ enabled: shouldShow && expanded });
  const count = data?.length ?? 0;

  if (!shouldShow) return null;

  const hasResults = count > 0;
  // Collapsed (and while generating) the title is an invitation, not a claim — the workflow has
  // not run yet, so promising N suggestions here would be a promise we cannot keep.
  const title = hasResults ? t('group_recommendations.banner_title', { count }) : t('group_recommendations.nudge_title');

  const describe = () => {
    if (hasResults) return t('group_recommendations.banner_description');
    if (isFetching) return t('group_recommendations.loading_recommendations');
    return t('group_recommendations.empty_state_description');
  };

  return {
    id: GROUP_NUDGE_ID,
    kind: 'recommendation',
    priority: 1,
    tagLabel: tHeader('aiRecommendations.recommendationTag'),
    title,
    dismiss,
    onExpand: () => setExpanded(true),
    render: () => (
      <NudgeMessage
        title={title}
        description={describe()}
        ctaLabel={hasResults ? t('group_recommendations.banner_cta') : undefined}
        onCta={
          hasResults
            ? () => {
                accept({ count });
                // The groups page owns the generate query and the dialog, so deep-link instead of
                // duplicating either here — same target `useAiRecommendationCta` used.
                history.push(`${Location.VEHICLES_GROUPS}?openGroupRecommendations=1`);
              }
            : undefined
        }
        dismissLabel={tHeader('aiRecommendations.dismiss')}
        onDismiss={() => dismiss('not-interested')}
      />
    ),
  };
};

export default useGroupNudge;
